import React, { useEffect } from 'react'

// components
import GroupDropdown from '../components/GroupDropdown'
import FilterTable from '../components/FilterTable'
import SegmentedToggle from '../components/SegmentedToggle'
import PositionChart from '../components/PositionChart'
import TimeChart, { AXIS_TIME } from '../components/TimeChart'
import WeatherForecast from '../components/WeatherForecast'
import { ButtonToolbar, Container, Header, Content } from 'rsuite'

// redux
import { useSelector, useDispatch } from 'react-redux'
import { getOptions, getYou, getContextOptions, getDisplayOptions, getShowWeather } from '../redux/options/selectors'  
import { getCarsAndGroups, getGroupList, getData } from '../redux/data/selectors'
import { setOption } from '../redux/options/actions'
import { useParams, useNavigate } from 'react-router'

/**
 * Graph of the positions in the group over time
 * @type {string}
 */
const GRAPH_POSITION = 'position'

/**
 * Graph of all laptimes of every car in the group, by time of day
 * @type {string}
 */
const GRAPH_LAPTIMES = 'laptimes'

/**
 * Graphs to choose from above the list
 * @type {Array<{ value: string, label: string, icon: string }>}
 */
const GRAPH_OPTIONS = [
  { value: GRAPH_POSITION, label: 'Position', icon: 'flag' },
  { value: GRAPH_LAPTIMES, label: 'Laptimes', icon: 'stopwatch' }
]

// Group page
const GroupPage = ( { context = 'grouppage' } ) => {

  // redux
  const dispatch = useDispatch()
  const { group = '', car = '', graph = GRAPH_POSITION } = getContextOptions( useSelector( getOptions ), context )
  const display = useSelector( getDisplayOptions )
  const cars = useSelector( getCarsAndGroups )
  const groups = useSelector( getGroupList )
  const loaded = useSelector( getData ).length > 0
  const you = useSelector( getYou )
  const { group: param } = useParams()
  const navigate = useNavigate()

  /**
   * Find a group in the list, comparing as text since url params are text
   * @param {number|string} value group to find
   * @returns {number|string|undefined} the group from the list, undefined when it has no laps
   */
  const findGroup = ( value ) => groups.find( g => String( g ) === String( value ) )

  // choose the group: the one in the url, the chosen one, the one of your car, or the first group;
  // a group without laps in this contest, such as one from another contest, falls back the same way
  useEffect( () =>
  {
    // reset car if needed
    if ( car )
    {
      dispatch( setOption( 'car', '', context ) )
    }

    // a race without groups or favourites shows all cars; before the laps are loaded, wait
    if ( !groups.length )
    {
      if ( !loaded ) return
      if ( group !== '' ) dispatch( setOption( 'group', '', context ) )
      if ( param ) navigate( '/group' )
      return
    }

    const chosen = findGroup( param ) ?? findGroup( group ) ?? ( cars.hasOwnProperty( you ) ? findGroup( cars[ you ] ) : undefined ) ?? groups[ 0 ]

    if ( String( chosen ) !== String( group ) )
    {
      dispatch( setOption( 'group', chosen, context ) )
    }

    if ( String( chosen ) !== String( param ) )
    {
      navigate( '/group/' + chosen )
    }
  }, [ param, group, groups, loaded ] )

  // the chosen graph, the position graph unless the laptimes are chosen
  const shown_graph = graph === GRAPH_LAPTIMES ? GRAPH_LAPTIMES : GRAPH_POSITION
  const laptimes = shown_graph === GRAPH_LAPTIMES

  // modify what to show; every lap with the laptimes, otherwise only positions and no penalties
  const modifyRows = data => laptimes ? data : data.filter( d => !isNaN( parseInt( d.pos ) ) && !isNaN( parseInt( d.penalty ) ) )

  // hide certain rows; with all laptimes the cones show, as the graph marks those laps
  const hide_rows = laptimes ? [] : [ 'penalty' ]
  const modifyColumns = columns => columns.map( c => (
    {
      ...c,
      show: !c.show ? false : !hide_rows.includes( c.name )
    }
  ) )

  // set column options
  const column_options = {
    id: true,
    lap: false,
    pos: true
  } 

  // the graph if the group graph is shown at all, with the weather forecast below it; without a
  // graph the forecast takes its place
  const showWeather = useSelector( getShowWeather )
  const forecast = showWeather ? <WeatherForecast/> : null
  const chart = !display.position_graph ? forecast : shown_graph === GRAPH_LAPTIMES ? <TimeChart max_rows={ 0 } axis={ AXIS_TIME }/> : <PositionChart/>

  return (
    <Container className={ context }>
      <Header>
        <ButtonToolbar className="filters">
          { groups.length > 0 && <GroupDropdown showAll={ false } context={ context } onChange={ value => navigate( '/group/' + value ) }/> }
          { display.position_graph && (
            <SegmentedToggle
              className="graph-toggle"
              options={ GRAPH_OPTIONS }
              selected={ shown_graph }
              onChange={ value => dispatch( setOption( 'graph', value, context ) ) }
            />
          ) }
        </ButtonToolbar>
      </Header>
      <Content>        
        <FilterTable 
          modifyRows={ modifyRows } 
          modifyColumns={ modifyColumns } 
          context={ context }
          column_options={ column_options }
          between={ chart }
        />
      </Content>
    </Container>
  )
}
export default GroupPage